/**
 * Old Marylebone Town Hall room definitions. The list is shared between:
 *   - change-room: which renders each room as a picker option and uses the
 *     per-service-type global_service_id to drive the reschedule.
 *   - change-booking: which uses the union of all OMTH global_service_ids to
 *     decide whether to show the "room" change checkbox at all (you can only
 *     change the room when your booking is at OMTH in the first place).
 */
export interface RoomDefinition {
  resource_id: string;
  pretty_name: string;
  marriage_global_service_id: number;
  civil_partnership_global_service_id: number;
  non_stat_civil_partnership_conversion: number;
  non_stat_naming: number;
  non_stat_commitment: number;
  non_stat_renewal_of_vows: number;
  /**
   * Person resource ids whose availability covers this room. 15333 ("All
   * rooms") is appended last on every room.
   */
  person_ids: number[];
  /**
   * Wood-panelled rooms run 30 minutes longer than the others. Moving a booking
   * into one of these rooms adds 30 minutes to its duration; moving out of one
   * removes 30 minutes (see getRescheduleDurationDelta).
   */
  wood_panelled_room: boolean;
  image: string;
  description: string;
}

export const ROOM_DEFINITIONS: RoomDefinition[] = [
  {
    resource_id: '6',
    pretty_name: 'The Westminster Room',
    marriage_global_service_id: 48375,
    civil_partnership_global_service_id: 48403,
    non_stat_civil_partnership_conversion: 48457,
    non_stat_naming: 48471,
    non_stat_commitment: 48483,
    non_stat_renewal_of_vows: 48497,
    person_ids: [15317, 15333],
    wood_panelled_room: true,
    image: 'https://res.cloudinary.com/bookinglab/image/upload/v1748801221/westminster_2_qmygf9.jpg',
    description:
      '<p>This is the largest room, seating up to 100 guests. Rich wood panelling, an impressive elaborate plasterwork ceiling and our resident grand piano invoke a sense of grandeur whilst a plush carpet adds to the luxurious feel of the room.</p><p><strong>Capacity</strong></p><p>Westminster - couple plus 100 guests. Capacity cannot the be exceeded and layout cannot be altered.</p>',
  },
  {
    resource_id: '7',
    pretty_name: 'The Knightsbridge Room',
    marriage_global_service_id: 48377,
    civil_partnership_global_service_id: 48405,
    non_stat_civil_partnership_conversion: 48455,
    non_stat_naming: 48469,
    non_stat_commitment: 48481,
    non_stat_renewal_of_vows: 48495,
    person_ids: [15316, 15337, 15333],
    wood_panelled_room: false,
    image: 'https://res.cloudinary.com/bookinglab/image/upload/v1748803910/knightsbridge_2_0_igfwxs.jpg',
    description:
      '<p>Panelled plaster walls and feature fireplace decorated in muted greys are complemented by rich velvet seating for up to 30 guests. Bold light fittings give a sense of drama making this room perfect for more decorous occasions.</p><p><strong>Capacity</strong></p><p>Knightsbridge - couple plus 30 guests. Capacity cannot the be exceeded and layout cannot be altered.</p>',
  },
  {
    resource_id: '11',
    pretty_name: 'The Marylebone Room',
    marriage_global_service_id: 48381,
    civil_partnership_global_service_id: 48409,
    non_stat_civil_partnership_conversion: 48463,
    non_stat_naming: 48477,
    non_stat_commitment: 48488,
    non_stat_renewal_of_vows: 48503,
    person_ids: [15317, 15327, 15333],
    wood_panelled_room: true,
    image: 'https://res.cloudinary.com/bookinglab/image/upload/v1759821658/OMTH__26_Mayfair_Library_interiors_-7_dp6rjz.jpg',
    description:
      '<p>Traditional wood panelling and a beautiful carved wood fireplace provide an impressive background for gorgeous photographs. A beautifully upholstered sofa and chairs provide seating for up to 12 guests, perfect for intimate ceremonies.</p><p><strong>Capacity</strong></p><p>Marylebone - couple plus 12 guests. Capacity cannot the be exceeded and layout cannot be altered.</p>',
  },
  {
    resource_id: '8',
    pretty_name: 'The Mayfair Room',
    marriage_global_service_id: 48421,
    civil_partnership_global_service_id: 48419,
    non_stat_civil_partnership_conversion: 48467,
    non_stat_naming: 48479,
    non_stat_commitment: 48493,
    non_stat_renewal_of_vows: 48507,
    person_ids: [15318, 15337, 15333],
    wood_panelled_room: false,
    image: 'https://res.cloudinary.com/bookinglab/image/upload/v1749636292/mayfair_0_vihdgg.jpg',
    description:
      '<p>The Mayfair Room reflects the warmth of rose gold and plush aubergine velvet in its décor with formal seating for up to 30 guests. For those with an interest in pop culture, it was in this room that Paul McCartney married Linda Eastman.</p><p><strong>Capacity</strong></p><p>Mayfair - couple plus 30 guests. Capacity cannot the be exceeded and layout cannot be altered.</p>',
  },
  {
    resource_id: '12',
    pretty_name: 'The Paddington Room',
    marriage_global_service_id: 48385,
    civil_partnership_global_service_id: 48411,
    non_stat_civil_partnership_conversion: 48465,
    non_stat_naming: 48509,
    non_stat_commitment: 48491,
    non_stat_renewal_of_vows: 48505,
    person_ids: [15317, 15333],
    wood_panelled_room: true,
    image: 'https://res.cloudinary.com/bookinglab/image/upload/v1760447122/paddington-room-old-marylebone-town-hall-with-diamond-motif-wood-panelling-2_unftdy.webp',
    description:
      '<p>Original wood panelling and marble fireplace with relaxed seating for up to 8 guests. The combination of traditional and contemporary provides a distinctive setting for relaxed ceremonies.</p><p><strong>Capacity</strong></p><p>Paddington - couple plus 8 guests. Capacity cannot the be exceeded and layout cannot be altered.</p>',
  },
  {
    resource_id: '9',
    pretty_name: 'The Pimlico Room',
    marriage_global_service_id: 48423,
    civil_partnership_global_service_id: 48417,
    non_stat_civil_partnership_conversion: 48461,
    non_stat_naming: 48475,
    non_stat_commitment: 48485,
    non_stat_renewal_of_vows: 48501,
    person_ids: [15318, 15337, 15333],
    wood_panelled_room: false,
    image: 'https://res.cloudinary.com/bookinglab/image/upload/v1749636990/pimlico_0_ohyfs6.jpg',
    description:
      '<p>Soft pastel walls are complimented with highlights of gold in the furnishings. Luxurious chairs arranged in an L shape provide seating for up to 20 guests. The stunning fireplace is surmounted by an antique mirror which beautifully reflects the gold chandelier whose shape represents the exchange of wedding rings.</p><p><strong>Capacity</strong></p><p>Pimlico - couple plus 20 guests. Capacity cannot the be exceeded and layout cannot be altered.</p>',
  },
  {
    resource_id: '10',
    pretty_name: 'The Soho Room',
    marriage_global_service_id: 48379,
    civil_partnership_global_service_id: 48407,
    non_stat_civil_partnership_conversion: 48459,
    non_stat_naming: 48473,
    non_stat_commitment: 48484,
    non_stat_renewal_of_vows: 48499,
    person_ids: [15316, 15337, 15333],
    wood_panelled_room: false,
    image: 'https://res.cloudinary.com/bookinglab/image/upload/v1749637089/soho_1_q5yrqg.jpg',
    description:
      '<p>The bright and airy feel of this room is enhanced by the light from the gold rings chandelier reflecting in the vintage mirror which sits on top of an intricately carved mantelpiece. Easy chairs and a sofa provide seating for up to 13 guests in a relaxed and intimate atmosphere.</p><p><strong>Capacity</strong></p><p>Soho - couple plus 13 guests. Capacity cannot the be exceeded and layout cannot be altered.</p>',
  },
];

/** Every OMTH room's global_service_id, across all service types. */
export const OMTH_GLOBAL_SERVICE_IDS: ReadonlySet<number> = new Set<number>(
  ROOM_DEFINITIONS.flatMap((d) => [
    d.marriage_global_service_id,
    d.civil_partnership_global_service_id,
    d.non_stat_civil_partnership_conversion,
    d.non_stat_naming,
    d.non_stat_commitment,
    d.non_stat_renewal_of_vows,
  ]),
);

/**
 * Resolves the OMTH global_service_id for a room given the booking's service
 * name. Each room carries one id per service type; the service name selects
 * which field to use. The "Civil Partnership Conversion" check must come before
 * the plain "Civil Partnership" check because the former contains the latter.
 * Defaults to marriage when the name doesn't match a known non-marriage type.
 */
export function getRoomGlobalServiceId(
  def: RoomDefinition,
  serviceName: string | null | undefined,
): number {
  const name = serviceName ?? '';
  if (/civil partnership conversion/i.test(name)) return def.non_stat_civil_partnership_conversion;
  if (/civil partnership/i.test(name)) return def.civil_partnership_global_service_id;
  if (/naming/i.test(name)) return def.non_stat_naming;
  if (/commitment/i.test(name)) return def.non_stat_commitment;
  if (/renewal of vows/i.test(name)) return def.non_stat_renewal_of_vows;
  return def.marriage_global_service_id;
}

export function isOmthService(globalId: number | string | null | undefined): boolean {
  if (globalId == null) return false;
  return OMTH_GLOBAL_SERVICE_IDS.has(Number(globalId));
}

/** Resource ids of the OMTH rooms that run 30 minutes longer (wood-panelled). */
export const WOOD_PANELLED_RESOURCE_IDS: ReadonlySet<number> = new Set<number>(
  ROOM_DEFINITIONS.filter((d) => d.wood_panelled_room).map((d) => Number(d.resource_id)),
);

/**
 * True when the given resource id is one of the wood-panelled OMTH rooms. Rooms
 * that aren't OMTH (or aren't recognised) return false.
 */
export function isWoodPanelledRoom(resourceId: number | string | null | undefined): boolean {
  if (resourceId == null) return false;
  return WOOD_PANELLED_RESOURCE_IDS.has(Number(resourceId));
}

/** The duration adjustment, in minutes, applied to a wood-panelled room. */
export const WOOD_PANELLED_DURATION_DELTA_MINUTES = 30;

/**
 * The change in ceremony duration (in minutes) when moving a booking from one
 * room to another, based on each room's wood-panelled status:
 *   - moving INTO a wood-panelled room from a non-wood-panelled one → +30
 *   - moving OUT of a wood-panelled room into a non-wood-panelled one → -30
 *   - moving between two rooms of the same type → 0
 */
export function getRescheduleDurationDelta(
  fromResourceId: number | string | null | undefined,
  toResourceId: number | string | null | undefined,
): number {
  const fromWood = isWoodPanelledRoom(fromResourceId);
  const toWood = isWoodPanelledRoom(toResourceId);
  if (fromWood === toWood) return 0;
  return toWood ? WOOD_PANELLED_DURATION_DELTA_MINUTES : -WOOD_PANELLED_DURATION_DELTA_MINUTES;
}
